import { Text } from '../../ui';
import { useTranslation } from 'react-i18next';
import { useApp, useError, useSnack } from '../../hooks';
import { Stack } from '@koob/margaret';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  KoobButton,
  KoobInputSegmented,
  KoobInputStepperField,
} from '@koob/ui';
import { useEffect, useState } from 'react';
import {
  getOrganization,
  updateOrganization,
} from '../../api/node/organization';
import { useAsync } from 'react-async';
import styled from 'styled-components';

const DurationText = styled(Text)`
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => theme.secondaryLight};
    text-decoration-color: ${({ theme }) => theme.primary};
  }
`;

const TosMaximumTimeBeforeBooking = ({ isAllowedToManage, organizationId }) => {
  const { t } = useTranslation('organization');
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const { currentOrganizationId } = useApp();
  const orgaId = organizationId ?? currentOrganizationId;
  const { data, reload } = useAsync({
    promiseFn: getOrganization,
    id: orgaId,
    filters: {
      withDetails: false,
    },
    watch: orgaId,
  });
  const currentOrganization = data?.data;
  const maxReservationInterval = currentOrganization?.maxReservationInterval;
  const [shouldEdit, setShouldEdit] = useState(true);

  useEffect(() => {
    if (
      maxReservationInterval === undefined ||
      maxReservationInterval === null
    ) {
      return;
    }
    setShouldEdit(false);
  }, [maxReservationInterval]);

  let duration;
  let durationUnit;
  if (maxReservationInterval >= 0) {
    durationUnit =
      maxReservationInterval >= 24
        ? t('misc:duration.day')
        : t('misc:duration.hour');
    duration =
      maxReservationInterval >= 24
        ? Math.ceil(maxReservationInterval / 24)
        : maxReservationInterval;
  }

  const updateMaxReservationInterval = async (values) => {
    setShouldEdit(false);
    let amount = values.amount;
    if (values.duration === 'day') {
      amount *= 24;
    }
    try {
      const input = {
        maxReservationInterval: amount,
        canBeRequestedForToAccess:
          currentOrganization?.canBeRequestedForToAccess,
      };
      await updateOrganization(orgaId, input);
      notify(t('organizationUpdateSuccess'));
      reload();
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  return shouldEdit ? (
    <Formik
      initialValues={{
        amount: duration || 0,
        duration:
          maxReservationInterval != null && maxReservationInterval < 24
            ? 'hour'
            : 'day',
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        amount: Yup.number().required(t('errors:required')),
      })}
      onSubmit={updateMaxReservationInterval}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form>
          <Stack direction="column" gutterSize={1} alignX="center">
            <Stack
              alignY={'center'}
              gutterSize={0.75}
              style={{ flexFlow: 'row wrap' }}
            >
              <Text>{t('distribution.distribTO1')}</Text>
              <KoobInputStepperField name="amount" min={1} />
              <KoobInputSegmented
                options={[
                  { label: t('misc:duration.day'), value: 'day' },
                  { label: t('misc:duration.hour'), value: 'hour' },
                ]}
                value={values.duration}
                onChange={(value) => setFieldValue('duration', value)}
              />
              <Text>{t('distribution.distribTO2')}</Text>
            </Stack>
            <Stack gutterSize={1}>
              <KoobButton
                variant="secondary"
                type="button"
                onClick={() => setShouldEdit(false)}
              >
                {t('misc:cancel')}
              </KoobButton>
              <KoobButton
                variant="primary"
                type="submit"
                loading={isSubmitting}
              >
                {t('misc:saveChanges')}
              </KoobButton>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  ) : (
    <Stack
      alignY={'center'}
      style={{ flexFlow: 'row wrap', justifyContent: 'start' }}
    >
      <Text type={'h3'}>{t('distribution.distribTO1')}&nbsp;</Text>&nbsp;
      <DurationText
        type={'h3'}
        onClick={() => {
          if (!isAllowedToManage) {
            return;
          }
          setShouldEdit(true);
        }}
      >
        {`${duration} ${durationUnit}`}
      </DurationText>
      &nbsp;
      <Text type={'h3'}>{t('distribution.distribTO2')}&nbsp;</Text>
    </Stack>
  );
};

export default TosMaximumTimeBeforeBooking;
